import { useEffect, useMemo, useRef, useState } from "react";
import {
  BatchFrame,
  BatchSectionRequest,
  SectionDescriptor,
  streamBatch,
} from "../../api/analyticsStream";

export type SectionState<T> =
  | { status: "pending" }
  | { status: "ok"; data: T; rows: number }
  | { status: "error"; message: string };

export interface StreamProgress {
  /** Every section on the page, not just the ones in flight. */
  total: number;
  /** Settled, whether they built or failed. */
  done: number;
  failed: number;
  /** Rows gathered so far, counted from the payloads themselves. */
  rows: number;
  running: boolean;
}

export type Descriptors = Record<string, SectionDescriptor<any>>;

export type SectionStates<D extends Descriptors> = {
  [K in keyof D]: D[K] extends SectionDescriptor<infer T>
    ? SectionState<T>
    : never;
};

/**
 * What identifies a section's request. Two descriptors with the same hash would
 * produce the same rows, so one already answered need not be asked again.
 */
export const hashDescriptor = (descriptor: SectionDescriptor<any>): string =>
  `${descriptor.kind}:${JSON.stringify(descriptor.query)}`;

/**
 * Works out which sections the next stream has to carry.
 *
 * This is the part that keeps one stream per tab from being a step backwards.
 * The widgets used to own the controls that shape their own request — trending's
 * top-N, the trend chart's granularity — and re-ask for one endpoint when they
 * changed. Those controls now live on the page so it can build the whole batch,
 * and without diffing, nudging top-N would rebuild all ten sections: seconds on a
 * warmed month, minutes on a custom quarter.
 *
 * Pure, so the behaviour can be tested without a stream or a DOM.
 */
export const planStream = (
  previous: Record<string, string>,
  descriptors: Descriptors
): { hashes: Record<string, string>; toFetch: string[]; removed: string[] } => {
  const hashes: Record<string, string> = {};
  const toFetch: string[] = [];

  Object.entries(descriptors).forEach(([id, descriptor]) => {
    const hash = hashDescriptor(descriptor);
    hashes[id] = hash;
    if (previous[id] !== hash) {
      toFetch.push(id);
    }
  });

  const removed = Object.keys(previous).filter((id) => !(id in hashes));

  return { hashes, toFetch, removed };
};

const UNREACHABLE =
  "Could not reach the server. The range may be too large to gather.";
const NEVER_ARRIVED = "The batch ended before this section arrived.";
const OUT_OF_TIME =
  "Not gathered — the batch ran out of time. Try a shorter range.";

// One instance, so a section that stays pending keeps the same object across
// renders and the memos downstream of it are not invalidated for nothing.
const PENDING: SectionState<never> = { status: "pending" };

/** A stream on the wire, against the hashes it was opened for. */
interface LiveStream {
  launched: Record<string, string>;
  delivered: Set<string>;
}

/**
 * Cancels a stream and forgets the hashes of everything it never delivered, so
 * the next plan asks for those sections again.
 *
 * The rewind is the whole point. Without it an aborted stream leaves its sections
 * recorded as already-requested while nothing is coming to fill them — which is
 * exactly how StrictMode's double mount (setup, cleanup, setup) left every tab
 * spinning forever: the teardown cancelled the only stream, and the second pass
 * saw the hashes and asked for nothing.
 */
const abortAndRewind = (
  controller: AbortController,
  stream: LiveStream,
  hashes: Record<string, string>
) => {
  controller.abort();
  Object.keys(stream.launched).forEach((id) => {
    if (!stream.delivered.has(id) && hashes[id] === stream.launched[id]) {
      delete hashes[id];
    }
  });
};

export const useAnalyticsStream = <D extends Descriptors>(
  descriptors: D
): { state: SectionStates<D>; progress: StreamProgress } => {
  const [state, setState] = useState<Record<string, SectionState<any>>>({});
  const [running, setRunning] = useState(false);
  const hashesRef = useRef<Record<string, string>>({});

  // Every stream currently on the wire, against the hashes it was opened for.
  // More than one can be live: a control nudged while a batch is still gathering
  // opens a second stream rather than throwing the first one away.
  const liveRef = useRef(new Map<AbortController, LiveStream>());

  // The page rebuilds `descriptors` every render, so the effect keys off what the
  // requests actually are rather than off object identity.
  const signature = useMemo(
    () =>
      JSON.stringify(
        Object.keys(descriptors)
          .sort()
          .map((id) => [id, hashDescriptor(descriptors[id])])
      ),
    [descriptors]
  );

  useEffect(() => {
    const plan = planStream(hashesRef.current, descriptors);
    hashesRef.current = plan.hashes;

    setState((previous) => {
      const next: Record<string, SectionState<any>> = {};
      Object.keys(plan.hashes).forEach((id) => {
        next[id] = plan.toFetch.includes(id) ? PENDING : previous[id] ?? PENDING;
      });
      return next;
    });

    // Only a stream with nothing left to contribute is cancelled. Aborting on
    // every change is what made nudging one dropdown throw away every section
    // still gathering — and on the long ranges this exists for, that is minutes
    // of server work discarded, where each widget used to own its own request.
    liveRef.current.forEach((stream, controller) => {
      const stillWanted = Object.keys(stream.launched).some(
        (id) => hashesRef.current[id] === stream.launched[id]
      );
      if (!stillWanted) {
        abortAndRewind(controller, stream, hashesRef.current);
        liveRef.current.delete(controller);
      }
    });

    if (plan.toFetch.length === 0) {
      return;
    }

    const controller = new AbortController();
    const launched: Record<string, string> = {};
    plan.toFetch.forEach((id) => {
      launched[id] = plan.hashes[id];
    });
    const delivered = new Set<string>();
    liveRef.current.set(controller, { launched, delivered });

    const sections: BatchSectionRequest[] = plan.toFetch.map((id) => ({
      id,
      descriptor: descriptors[id],
    }));

    // Still the section this stream was opened for, rather than one a later
    // stream has already taken over.
    const isCurrent = (id: string) => hashesRef.current[id] === launched[id];

    /**
     * Anything this stream was asked for and never delivered. A pending section
     * with nothing left to deliver it is a spinner that turns forever, which is
     * the failure this whole change exists to remove.
     */
    const failUndelivered = (reason: string) =>
      setState((previous) => {
        const next = { ...previous };
        Object.keys(launched).forEach((id) => {
          if (!delivered.has(id) && isCurrent(id) && next[id]?.status === "pending") {
            next[id] = { status: "error", message: reason };
          }
        });
        return next;
      });

    const run = async () => {
      setRunning(true);
      let terminal: Extract<BatchFrame, { t: "done" }> | null = null;

      try {
        for await (const frame of streamBatch(sections, controller.signal)) {
          if (frame.t === "done") {
            terminal = frame;
            continue;
          }
          if (frame.t !== "section" || !isCurrent(frame.id)) {
            continue;
          }

          const descriptor = descriptors[frame.id];
          const result: SectionState<any> = frame.ok
            ? (() => {
                const data = descriptor.adapt(frame.data);
                return { status: "ok", data, rows: descriptor.count(data) };
              })()
            : { status: "error", message: frame.error };

          delivered.add(frame.id);
          setState((previous) => ({ ...previous, [frame.id]: result }));
        }

        // The server names what its budget left unstarted, and reports a loop
        // that died as `fatal`. Either way the stream ends cleanly, so nothing
        // throws and only this reconciliation notices.
        if (terminal?.fatal) {
          failUndelivered(terminal.fatal);
        } else if (terminal?.pending?.length) {
          failUndelivered(OUT_OF_TIME);
        } else {
          failUndelivered(NEVER_ARRIVED);
        }
      } catch (error) {
        if (controller.signal.aborted) {
          return;
        }

        console.error("Analytics batch stream failed:", error);
        failUndelivered(UNREACHABLE);
      } finally {
        liveRef.current.delete(controller);
        setRunning(liveRef.current.size > 0);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  // Leaving the page is the one time everything should stop: the abort closes
  // the reader and the server's RequestAborted cancels whatever query is still
  // running against Postgres.
  useEffect(() => {
    const live = liveRef.current;
    const hashes = hashesRef.current;
    return () => {
      live.forEach((stream, controller) =>
        abortAndRewind(controller, stream, hashes)
      );
      live.clear();
    };
  }, []);

  // Every id the caller asked for, always, from the very first render. The effect
  // that fills `state` runs after that render, so reading it raw would hand the
  // sections `undefined` — and they read `section.status` inside useMemo, during
  // render, where there is no guard to reach. The prop type promises a state; this
  // is what keeps that promise.
  const settled = useMemo(() => {
    const next: Record<string, SectionState<any>> = {};
    Object.keys(descriptors).forEach((id) => {
      next[id] = state[id] ?? PENDING;
    });
    return next;
  }, [state, descriptors]);

  const progress = useMemo<StreamProgress>(() => {
    const entries = Object.values(settled);
    return {
      total: entries.length,
      done: entries.filter((s) => s.status !== "pending").length,
      failed: entries.filter((s) => s.status === "error").length,
      rows: entries.reduce(
        (sum, s) => sum + (s.status === "ok" ? s.rows : 0),
        0
      ),
      running,
    };
  }, [settled, running]);

  return { state: settled as SectionStates<D>, progress };
};
